import React, { useCallback, useEffect, useRef, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { notificationService } from '../../api';
import { Bell, CheckCheck, Filter, Info, AlertTriangle, CheckCircle, XCircle, BookOpen, Award, FileText, Megaphone } from 'lucide-react';
import toast from 'react-hot-toast';

// ── Category config ───────────────────────────────────────────────────────────
const CATEGORY_CONFIG = {
  info:        { icon: Info,         color: '#2FA7A1', bg: '#2FA7A115', label: 'Info' },
  success:     { icon: CheckCircle,  color: '#10b981', bg: '#10b98115', label: 'Success' },
  warning:     { icon: AlertTriangle,color: '#f59e0b', bg: '#f59e0b15', label: 'Warning' },
  error:       { icon: XCircle,      color: '#ef4444', bg: '#ef444415', label: 'Error' },
  enrollment:  { icon: BookOpen,     color: '#2F7C7B', bg: '#2F7C7B15', label: 'Enrollment' },
  assessment:  { icon: FileText,     color: '#F2B542', bg: '#f9731615', label: 'Assessment' },
  certificate: { icon: Award,        color: '#f59e0b', bg: '#f59e0b15', label: 'Certificate' },
  announcement:{ icon: Megaphone,    color: '#2FA7A1', bg: '#2FA7A115', label: 'Announcement' },
};

function NotifCard({ notif, onMarkRead, onMarkUnread }) {
  const cfg = CATEGORY_CONFIG[notif.category] || CATEGORY_CONFIG.info;
  const Icon = cfg.icon;
  return (
    <div
      style={{
        display: 'flex',
        gap: 16,
        padding: '16px 20px',
        borderRadius: 14,
        background: notif.is_read ? 'var(--color-bg-secondary)' : `${cfg.bg}`,
        border: `1px solid ${notif.is_read ? 'var(--color-border)' : cfg.color + '55'}`,
        transition: 'all 0.2s',
        position: 'relative',
      }}
    >
      {/* Unread dot */}
      {!notif.is_read && (
        <div style={{
          position: 'absolute', top: 14, right: 14,
          width: 8, height: 8, borderRadius: '50%',
          background: cfg.color,
        }} />
      )}

      {/* Icon */}
      <div style={{
        flexShrink: 0, width: 40, height: 40, borderRadius: 10,
        background: cfg.bg, border: `1px solid ${cfg.color}33`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={18} style={{ color: cfg.color }} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {notif.subject && (
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 3 }}>
            {notif.subject}
          </div>
        )}
        <div style={{ fontSize: 13, color: notif.is_read ? 'var(--color-text-secondary)' : 'var(--color-text-primary)', lineHeight: 1.5 }}>
          {notif.message}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{notif.time_ago}</span>
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
            background: cfg.bg, color: cfg.color,
          }}>{cfg.label}</span>
          <button
            onClick={() => notif.is_read ? onMarkUnread(notif.id) : onMarkRead(notif.id)}
            style={{
              fontSize: 11, background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--color-text-secondary)', textDecoration: 'underline', padding: 0,
            }}
          >
            {notif.is_read ? 'Mark unread' : 'Mark read'}
          </button>
        </div>
      </div>
    </div>
  );
}

const FILTERS = ['all', 'unread', 'info', 'success', 'warning', 'error', 'enrollment', 'assessment', 'certificate', 'announcement'];

export default function NotificationInbox() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const PAGE_SIZE = 20;

  const fetchNotifs = useCallback(async (reset = false) => {
    try {
      setLoading(true);
      const params = { page: reset ? 1 : page, page_size: PAGE_SIZE };
      if (filter === 'unread') params.is_read = 'false';
      else if (filter !== 'all') params.category = filter;

      const res = await notificationService.getMyNotifications(params);
      const results = res.data.results || res.data;
      const count = res.data.count || results.length;
      setNotifications(reset ? results : (prev) => [...prev, ...results]);
      setHasMore(count > (reset ? 1 : page) * PAGE_SIZE);
      if (reset) setPage(1);
    } catch {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => { fetchNotifs(true); }, [filter]); // eslint-disable-line

  const handleMarkRead = async (id) => {
    try {
      await notificationService.markRead(id);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
    } catch { toast.error('Failed to mark as read'); }
  };

  const handleMarkUnread = async (id) => {
    try {
      await notificationService.markUnread(id);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, is_read: false } : n));
    } catch { toast.error('Failed to mark as unread'); }
  };

  const handleMarkAllRead = async () => {
    try {
      setMarkingAll(true);
      await notificationService.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch { toast.error('Failed to mark all as read'); } finally { setMarkingAll(false); }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <DashboardLayout>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Bell size={22} />
              Notifications
              {unreadCount > 0 && (
                <span style={{ background: '#2F7C7B', color: '#fff', borderRadius: 99, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                  {unreadCount} new
                </span>
              )}
            </h1>
            <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)', fontSize: 13 }}>
              Your personalised notification inbox.
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              id="mark-all-read-btn"
              onClick={handleMarkAllRead}
              disabled={markingAll}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                background: '#2F7C7B', color: '#fff', border: 'none', cursor: 'pointer',
              }}
            >
              <CheckCheck size={15} />
              {markingAll ? 'Marking…' : 'Mark all read'}
            </button>
          )}
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {FILTERS.map((f) => (
            <button
              key={f}
              id={`notif-filter-${f}`}
              onClick={() => setFilter(f)}
              style={{
                padding: '6px 14px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                border: '1px solid var(--color-border)',
                background: filter === f ? '#2F7C7B' : 'transparent',
                color: filter === f ? '#fff' : 'var(--color-text-secondary)',
                cursor: 'pointer', transition: 'all 0.2s',
                textTransform: 'capitalize',
              }}
            >
              {f}
            </button>
          ))}
        </div>

        {/* List */}
        {loading && notifications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--color-text-secondary)' }}>
            Loading notifications…
          </div>
        ) : notifications.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: 60,
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 16,
          }}>
            <Bell size={40} style={{ color: 'var(--color-text-secondary)', opacity: 0.4, marginBottom: 12 }} />
            <div style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>All clear!</div>
            <div style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginTop: 4 }}>No notifications here.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {notifications.map((n) => (
              <NotifCard key={n.id} notif={n} onMarkRead={handleMarkRead} onMarkUnread={handleMarkUnread} />
            ))}
            {hasMore && (
              <button
                onClick={() => { setPage((p) => p + 1); fetchNotifs(); }}
                style={{
                  padding: '12px', borderRadius: 12, marginTop: 8,
                  background: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text-secondary)',
                  cursor: 'pointer', fontWeight: 600, fontSize: 13,
                }}
              >
                Load more
              </button>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
